/**
 * スマホの下部固定タブ(■17 第1弾・2026-08-31 発注者確定)。
 *
 * 日常操作はこのタブで完結する: 申請 / 一覧 / 支払待ち / 受領待ち / 受領済み / やること / お知らせ。
 * (2026-10-15 発注者指示で「受領待ち」を追加: 自分のお金がまだ戻っていないものをすぐ見られるように)
 * (2026-10-14 発注者指示で「支払い」「受領」を追加。それまではハンバーガーメニューの中だけだった)
 * - 「申請」はタップで2択シート(経費申請 / 支払い済み申請(承認不要))
 * - 「やること」「お知らせ」は未処理・未読の件数バッジを表示
 * ハンバーガーメニュー(ドロワー)は従来どおり残す。
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Props = {
  /** やること(承認・支払い・受領・支払い済み申請確認・完了確認)の未処理合計 */
  todoCount: number;
  /** お知らせの未読件数 */
  newsCount: number;
  /** 支払い・受領の未処理件数(左メニューと同じ数) */
  payCount: number;
  receiveCount: number;
};

function Badge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      style={{
        position: 'absolute',
        top: -3,
        right: -10,
        minWidth: 16,
        height: 16,
        padding: '0 4px',
        borderRadius: 9,
        background: 'var(--red)',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** タブのアイコン(お知らせベルと同じ線画で統一) */
function Icon({ name }: { name: 'apply' | 'list' | 'pay' | 'receive' | 'wait' | 'todo' | 'news' }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (name === 'apply') {
    return (
      <svg {...common}>
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
        <path d="M14 3l5 5" />
        <path d="M14 3v5h5" />
        <path d="M18 14v6M15 17h6" />
      </svg>
    );
  }
  if (name === 'list') {
    return (
      <svg {...common}>
        <path d="M8 6h13M8 12h13M8 18h13" />
        <path d="M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
    );
  }
  if (name === 'pay') {
    // 支払い: お札
    return (
      <svg {...common}>
        <rect x="2.5" y="6" width="19" height="12" rx="2" />
        <circle cx="12" cy="12" r="2.6" />
        <path d="M6 9.5v5M18 9.5v5" />
      </svg>
    );
  }
  if (name === 'receive') {
    // 受領: 受け取り箱
    return (
      <svg {...common}>
        <path d="M12 3v10M8 9l4 4 4-4" />
        <path d="M4 14h4l1.5 2.5h5L16 14h4v4.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5z" />
      </svg>
    );
  }
  if (name === 'wait') {
    // 受領待ち: 時計
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 2" />
      </svg>
    );
  }
  if (name === 'todo') {
    return (
      <svg {...common}>
        <path d="M9 11l3 3 6-6" />
        <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export default function MobileTabBar({ todoCount, newsCount, payCount, receiveCount }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [applyOpen, setApplyOpen] = useState(false);

  const isApply = pathname === '/apply' || pathname === '/report';
  const isTodo = ['/todo', '/approve', '/acct-confirm', '/confirm'].includes(pathname);

  const tabs = [
    { key: 'apply', label: '申請', active: isApply, badge: 0, onClick: () => setApplyOpen(true) },
    { key: 'list', label: '一覧', active: pathname === '/mylist', badge: 0, onClick: () => navigate('/mylist') },
    // 5文字だと320px幅の端末で収まらないため、下部メニューだけ「支払待ち」と表記(2026-10-16)
    { key: 'pay', label: '支払待ち', active: pathname === '/pay', badge: payCount, onClick: () => navigate('/pay') },
    // お金の流れの順: 支払い → 受領待ち → 受領済み(2026-10-15 指示)
    { key: 'wait', label: '受領待ち', active: pathname === '/receive-wait', badge: receiveCount, onClick: () => navigate('/receive-wait') },
    { key: 'receive', label: '受領済み', active: pathname === '/receive', badge: 0, onClick: () => navigate('/receive') },
    { key: 'todo', label: 'やること', active: isTodo, badge: todoCount, onClick: () => navigate('/todo') },
    { key: 'news', label: 'お知らせ', active: pathname === '/news', badge: newsCount, onClick: () => navigate('/news') },
  ] as const;

  return (
    <>
      <nav className="tabbar" aria-label="メインナビゲーション">
        {tabs.map((t) => (
          <button
            key={t.key}
            className={`tabbar-item${t.active ? ' on' : ''}`}
            aria-current={t.active ? 'page' : undefined}
            onClick={t.onClick}
          >
            <span style={{ position: 'relative', lineHeight: 0 }}>
              <Icon name={t.key} />
              <Badge count={t.badge} />
            </span>
            <span className="tabbar-label">{t.label}</span>
          </button>
        ))}
      </nav>

      {/* 「申請」の2択シート(2026-08-31 指示) */}
      {applyOpen && (
        <div className="sheet-overlay" onClick={() => setApplyOpen(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-title">申請する</div>
            {[
              { label: '経費申請', note: '承認を受けてから支払う', to: '/apply' },
              { label: '支払い済み申請', note: '承認不要(支払い済みの事後報告)', to: '/report' },
            ].map((o) => (
              <button
                key={o.to}
                className="sheet-item"
                onClick={() => {
                  setApplyOpen(false);
                  navigate(o.to);
                }}
              >
                <b>{o.label}</b>
                <span className="hint">{o.note}</span>
              </button>
            ))}
            <button className="sheet-cancel" onClick={() => setApplyOpen(false)}>
              キャンセル
            </button>
          </div>
        </div>
      )}
    </>
  );
}
